// Professional Baseball Analytics API Service
// Leveraging enhanced v2 endpoints for comprehensive statistics

import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

// Dynamic current year — smart default: switch to current year on March 25 (regular season opener)
const CURRENT_YEAR = () => {
  const now = new Date();
  const month = now.getMonth(); // 0-indexed: Jan=0, Mar=2
  const day = now.getDate();
  const useCurrentYear = month > 2 || (month === 2 && day >= 25);
  return (useCurrentYear ? now.getFullYear() : now.getFullYear() - 1).toString();
};

// Create axios instance with default config
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// ============================================================================
// IN-MEMORY REQUEST CACHE — prevents duplicate fetches within a short window
// ============================================================================
const _cache = new Map();
const CACHE_TTL = 60_000; // 60 seconds

/**
 * Wrap a GET request with a short-lived in-memory cache.
 * Identical URLs hitting the API within CACHE_TTL share the same promise.
 */
const cachedGet = async (url) => {
  const now = Date.now();
  const entry = _cache.get(url);
  if (entry && now - entry.ts < CACHE_TTL) return entry.promise;

  const promise = apiClient.get(url).then(r => r.data);
  _cache.set(url, { promise, ts: now });

  // Auto-evict after TTL
  promise.catch(() => _cache.delete(url));
  setTimeout(() => { if (_cache.get(url)?.ts === now) _cache.delete(url); }, CACHE_TTL + 500);

  return promise;
};

/** Bust all cached entries (e.g. after year change) */
export const clearApiCache = () => _cache.clear();

// Simple network activity tracker for conditional route transition overlay
let pendingRequests = 0;
const listeners = new Set();
const notify = () => listeners.forEach(l => {
  try { l(pendingRequests); } catch (_) { /* noop */ }
});

export const networkActivity = {
  subscribe: (fn) => { listeners.add(fn); return () => listeners.delete(fn); },
  getPending: () => pendingRequests
};

// Request interceptor for debugging
apiClient.interceptors.request.use(
  (config) => {
  pendingRequests += 1; notify();
    return config;
  },
  (error) => {
    console.error('❌ API Request Error:', error);
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
apiClient.interceptors.response.use(
  (response) => {
  pendingRequests = Math.max(0, pendingRequests - 1); notify();
    return response;
  },
  (error) => {
    console.error('❌ API Response Error:', error.response?.data || error.message);
  pendingRequests = Math.max(0, pendingRequests - 1); notify();
    return Promise.reject(error);
  }
);

// ============================================================================
// ENHANCED STATISTICS API (v2)
// ============================================================================

export const statsApi = {
  // Composite dashboard endpoint — one request for all dashboard data
  getDashboard: async (year = CURRENT_YEAR()) => {
    return cachedGet(`/v2/stats/dashboard?year=${year}`);
  },

  // Weekly highlights — nastiest pitches, longest HRs, luckiest wins
  getWeeklyHighlights: async (year = CURRENT_YEAR(), days = 7) => {
    return cachedGet(`/v2/stats/highlights/weekly?year=${year}&days=${days}`);
  },

  // Database summary with enhanced metrics
  getSummary: async (year = CURRENT_YEAR()) => {
    return cachedGet(`/v2/stats/summary?year=${year}`);
  },

  // Statistical leaders with proper qualifications
  getLeaders: async (params = {}) => {
    const {
      category = 'batting',
      stat = 'avg',
      team,
      year = CURRENT_YEAR(),
      limit = 10,
      minGames = 10,
      minAtBats = 50,
      minInnings = 10,
      mode, // 'rolling' for rolling window
    } = params;

    const queryParams = new URLSearchParams({
      category,
      stat,
      year,
      limit: limit.toString(),
      minGames: minGames.toString(),
      ...(category === 'batting' && { minAtBats: minAtBats.toString() }),
      ...(category === 'pitching' && { minInnings: minInnings.toString() }),
      ...(team && { team }),
      ...(mode && { mode })
    });

    return cachedGet(`/v2/stats/leaders?${queryParams}`);
  },

  // CVR Movers — biggest rolling-vs-prev-year CVR risers/fallers
  getCVRMovers: async ({ year = CURRENT_YEAR(), limit = 10, minGames, minDelta, baseline, days, team } = {}) => {
    const qs = new URLSearchParams({ year: String(year), limit: String(limit) });
    if (minGames != null) qs.set('minGames', String(minGames));
    if (minDelta != null) qs.set('minDelta', String(minDelta));
    if (baseline) qs.set('baseline', baseline);
    if (days != null) qs.set('days', String(days));
    if (team) qs.set('team', team);
    return cachedGet(`/v2/stats/cvr-movers?${qs}`);
  },

  // The Cycle Index — team-level market efficiency leaderboard
  getCycleIndex: async ({ year = CURRENT_YEAR() } = {}) => {
    return cachedGet(`/v2/stats/cycle-index?year=${encodeURIComponent(year)}`);
  },

  // Advanced player analytics
  getAdvancedPlayerStats: async (playerId, year = CURRENT_YEAR()) => {
    const response = await apiClient.get(`/v2/stats/advanced/${playerId}?year=${year}`);
    return response.data;
  },

  // Player situational splits
  getPlayerSplits: async (playerId, params = {}) => {
    const { year = CURRENT_YEAR(), splitType = 'all' } = params;
    const response = await apiClient.get(`/v2/stats/splits/${playerId}?year=${year}&splitType=${splitType}`);
    return response.data;
  },

  // Advanced multi-player comparison
  comparePlayersAdvanced: async (players, params = {}) => {
    const { year = CURRENT_YEAR(), categories = ['batting', 'pitching'] } = params;
    const response = await apiClient.post('/v2/stats/compare/advanced', {
      players,
      year,
      categories
    });
    return response.data;
  },

  // Advanced team analytics
  getTeamAdvanced: async (teamId, year = CURRENT_YEAR()) => {
    const response = await apiClient.get(`/v2/stats/team/${teamId}/advanced?year=${year}`);
    return response.data;
  },

  // Enhanced CVR (Cycle Value Rating) API
  getCVR: async (playerA, playerB, year = CURRENT_YEAR()) => {
    const response = await apiClient.get(`/v2/stats/cvr/${encodeURIComponent(playerA)}/${encodeURIComponent(playerB)}?year=${year}`);
    return response.data;
  },

  // Individual player CVR
  getPlayerCVR: async (team, player, year = CURRENT_YEAR()) => {
    const response = await apiClient.get(`/v2/stats/cvr/${team}/${encodeURIComponent(player)}/${year}`);
    return response.data;
  },

  // Salary data API
  getSalary: async (team, player, year = CURRENT_YEAR()) => {
    const response = await apiClient.get(`/v2/stats/salary/${team}/${encodeURIComponent(player)}/${year}`);
    return response.data;
  },

  // Get all salaries for a team
  getTeamSalaries: async (team, year = CURRENT_YEAR()) => {
    const response = await apiClient.get(`/v2/stats/salary/team/${team}/${year}`);
    return response.data;
  },

  // Get all salaries for the year (league-wide)
  getAllSalaries: async (year = CURRENT_YEAR()) => {
    const response = await apiClient.get(`/v2/stats/salary/all/${year}`);
    return response.data;
  }
  ,
  // League baselines (dynamic league averages)
  getBaselines: async (year = CURRENT_YEAR()) => {
    const response = await apiClient.get(`/v2/stats/baselines?year=${year}`);
    return response.data;
  },

  // Savant-style league percentile rankings for a player
  getPercentiles: async (team, player, year = CURRENT_YEAR()) => {
    return cachedGet(`/v2/stats/percentiles/${team}/${encodeURIComponent(player)}/${year}`);
  }
};

// ============================================================================
// GAMES API (v2) - Live Scores and Schedules
// ============================================================================

export const gamesApi = {
  // Get recent games for scoreboard
  getRecentGames: async (params = {}) => {
    const { date, limit = 15 } = params;
    const queryParams = new URLSearchParams();
    
    if (date) queryParams.append('date', date);
    if (limit) queryParams.append('limit', limit.toString());
    
    const response = await apiClient.get(`/v2/games/recent?${queryParams}`);
    return response.data;
  },

  // Get currently live games
  getLiveGames: async () => {
    const response = await apiClient.get(`/v2/games/live`);
    return response.data;
  },

  // Get games for specific date
  getSchedule: async (date, limit = 50) => {
    const queryParams = new URLSearchParams();
    if (limit) queryParams.append('limit', limit.toString());
    
    const response = await apiClient.get(`/v2/games/schedule/${date}?${queryParams}`);
    return response.data;
  },

  // Get games for date range
  getGamesRange: async (startDate, endDate, limit = 50) => {
    const queryParams = new URLSearchParams({
      startDate,
      endDate,
      limit: limit.toString()
    });
    
    const response = await apiClient.get(`/v2/games/range?${queryParams}`);
    return response.data;
  },

  // Get MLB live schedule for a date (proxies MLB Stats API)
  getLiveSchedule: async (date) => {
    const response = await apiClient.get(`/v2/mlb-live/schedule/${date}`);
    return response.data;
  },

  // Get win probability timeline + WPA data for a game
  getWinProbability: async (gameId) => {
    if (!gameId) return null;
    const response = await apiClient.get(`/v2/mlb-live/win-probability/${gameId}`);
    return response.data;
  },

  // Get detailed boxscore (batting, pitching, play-by-play, linescore)
  getBoxscore: async (gameId) => {
    const response = await apiClient.get(`/v2/mlb-live/boxscore/${gameId}`);
    return response.data;
  },
};

// ============================================================================
// ENHANCED PLAYERS API (v2)
// ============================================================================

export const playersApi = {
  // Enhanced player listing with comprehensive filtering
  getPlayers: async (params = {}) => {
    const {
      team,
      year = CURRENT_YEAR(),
      position,
      status = 'active',
      sortBy = 'name',
      sortOrder = 'desc', // Add sortOrder parameter
      minGames = 0,
      category = 'batting',
      playerType = 'all', // Add playerType filtering
      startDate, // Add date range support
      endDate,   // Add date range support
      dateRange,  // Add date range support
      limit, // Add limit parameter
      gameType, // Game type filter: 'R', 'S', 'P', 'all'
      slim,      // If truthy, request compact payload (~90% smaller)
      mode       // 'rolling' for rolling window CVR
    } = params;

    const queryParams = new URLSearchParams({
      year,
      status,
      sortBy,
      sortOrder, // Add sortOrder to query
      minGames: minGames.toString(),
      category,
      playerType,
      ...(team && { team }),
      ...(position && { position }),
      ...(startDate && { startDate }),
      ...(endDate && { endDate }),
      ...(dateRange && { dateRange }),
      ...(limit && { limit: limit.toString() }),
      ...(gameType && { gameType }),
      ...(slim && { slim: '1' }),
      ...(mode && { mode })
    });

    return cachedGet(`/v2/players?${queryParams}`);
  },

  // ── Unified player profile (new: team/year agnostic) ──
  getUnifiedProfile: async (slug, params = {}) => {
    const { year, team, mode } = params;
    const qs = new URLSearchParams();
    if (year) qs.set('year', year);
    if (team) qs.set('team', team);
    if (mode) qs.set('mode', mode);
    const qsStr = qs.toString() ? `?${qs.toString()}` : '';
    const response = await apiClient.get(`/v2/players/profile/${slug}${qsStr}`);
    return response.data;
  },

  // Resolve a player name to their slug + index
  resolvePlayer: async (name) => {
    const response = await apiClient.get(`/v2/players/resolve/${encodeURIComponent(name)}`);
    return response.data;
  },

  // Unified player search (searches the player index)
  searchUnified: async (query, params = {}) => {
    const { team, position, limit = 20 } = params;
    const qs = new URLSearchParams({ q: query, limit: String(limit) });
    if (team) qs.set('team', team);
    if (position) qs.set('position', position);
    const response = await apiClient.get(`/v2/players/search-unified?${qs.toString()}`);
    return response.data;
  },

  // Comprehensive individual player data (legacy - team/year specific)
  getPlayer: async (team, playerName, year = CURRENT_YEAR()) => {
    const playerId = `${team}-${playerName}-${year}`;
    const response = await apiClient.get(`/v2/players/${playerId}`);
    return response.data;
  },

  // Player game log
  getPlayerGames: async (team, playerName, year = CURRENT_YEAR()) => {
    const playerId = `${team}-${playerName}-${year}`;
    const response = await apiClient.get(`/v2/players/${playerId}/games`);
    return response.data;
  },

  // Player vs team matchups
  getPlayerVsTeam: async (team, playerName, year, opponent) => {
    const playerId = `${team}-${playerName}-${year}`;
    const response = await apiClient.get(`/v2/players/${playerId}/vs/${opponent}`);
    return response.data;
  },

  // Legacy method
  getPlayerById: async (playerId, params = {}) => {
    const { year = CURRENT_YEAR(), includeGameLog = 'false' } = params;
    const response = await apiClient.get(`/v2/players/${playerId}?year=${year}&includeGameLog=${includeGameLog}`);
    return response.data;
  },

  // Player situational splits
  getPlayerSplits: async (team, playerName, year = CURRENT_YEAR(), splitType = 'all') => {
    const playerId = `${team}-${playerName}-${year}`;
    const response = await apiClient.get(`/v2/players/${playerId}/splits?splitType=${splitType}`);
    return response.data;
  },

  // Performance trends and projections
  getPlayerTrends: async (playerId, params = {}) => {
    const { year = CURRENT_YEAR(), period = '30' } = params;
    const response = await apiClient.get(`/v2/players/${playerId}/trends?year=${year}&period=${period}`);
    return response.data;
  },

  // Advanced multi-player comparison
  comparePlayers: async (players, params = {}) => {
    const { year = CURRENT_YEAR(), categories = ['batting'], metrics = 'advanced' } = params;
    const response = await apiClient.post('/v2/players/compare', {
      players,
      year,
      categories,
      metrics
    });
    return response.data;
  },

  // Team roster with comprehensive stats
  getTeamRoster: async (teamId, params = {}) => {
    const { year = CURRENT_YEAR(), sortBy = 'name', category = 'batting' } = params;
    const response = await apiClient.get(`/v2/players/team/${teamId}?year=${year}&sortBy=${sortBy}&category=${category}`);
    return response.data;
  },

  // Advanced player search
  searchPlayers: async (query, params = {}) => {
    const {
      year = CURRENT_YEAR(),
      team,
      position,
      minGames = 0,
      statThreshold,
      limit = 20
    } = params;

    const queryParams = new URLSearchParams({
      q: query,
      year,
      minGames: minGames.toString(),
      limit: limit.toString(),
      ...(team && { team }),
      ...(position && { position }),
      ...(statThreshold && { statThreshold })
    });

    const response = await apiClient.get(`/v2/players/search?${queryParams}`);
    return response.data;
  }
};

// ============================================================================
// ENHANCED TEAMS API (v2)
// ============================================================================

export const teamsApi = {
  // Enhanced team listing with comprehensive statistics
  getTeams: async (params = {}) => {
    const { year = CURRENT_YEAR(), league, division, sortBy = 'name', gameType, mode } = params;
    const queryParams = new URLSearchParams({
      year,
      sortBy,
      ...(league && { league }),
      ...(division && { division }),
      ...(gameType && { gameType }),
      ...(mode && { mode })
    });

    return cachedGet(`/v2/teams?${queryParams}`);
  },

  // Comprehensive individual team data
  getTeam: async (teamId, params = {}) => {
    const { year = CURRENT_YEAR(), includeRoster = 'false', mode } = params;
    const qs = new URLSearchParams({ year, includeRoster, ...(mode && { mode }) });
    const response = await apiClient.get(`/v2/teams/${teamId}?${qs}`);
    return response.data;
  },

  // Team schedule and game results
  getTeamSchedule: async (teamId, params = {}) => {
    const { year = CURRENT_YEAR(), month, limit = 50 } = params;
    const queryParams = new URLSearchParams({
      year,
      limit: limit.toString(),
      ...(month && { month })
    });

    const response = await apiClient.get(`/v2/teams/${teamId}/schedule?${queryParams}`);
    return response.data;
  },

  /** Team contract data — salary, service time, arb status, etc. */
  getTeamContracts: async (teamId, params = {}) => {
    const { year = CURRENT_YEAR(), sortBy = 'salary' } = params;
    const response = await apiClient.get(`/v2/teams/${encodeURIComponent(teamId)}/contracts?year=${year}&sortBy=${sortBy}`);
    return response.data;
  },

  // Upcoming scheduled games from MLB Stats API
  getTeamUpcoming: async (teamId, params = {}) => {
    const { days = 14 } = params;
    const response = await apiClient.get(`/v2/teams/${encodeURIComponent(teamId)}/upcoming?days=${days}`);
    return response.data;
  },

  // Win projections & probabilities for upcoming games
  getTeamProjections: async (teamId, params = {}) => {
    const { days = 30 } = params;
    const response = await apiClient.get(`/v2/teams/${encodeURIComponent(teamId)}/projections?days=${days}`);
    return response.data;
  },

  // Team situational splits
  getTeamSplits: async (teamId, params = {}) => {
    const { year = CURRENT_YEAR(), splitType = 'all' } = params;
    const response = await apiClient.get(`/v2/teams/${teamId}/splits?year=${year}&splitType=${splitType}`);
    return response.data;
  },

  // League standings with advanced metrics
  getStandings: async (params = {}) => {
    const { year = CURRENT_YEAR(), league, division, gameType } = params;
    const queryParams = new URLSearchParams({
      year,
      ...(league && { league }),
      ...(division && { division }),
      ...(gameType && { gameType })
    });

    const response = await apiClient.get(`/standings?${queryParams}`);
    return response.data;
  },

  // Advanced team comparison
  compareTeams: async (teams, params = {}) => {
    const { year = CURRENT_YEAR(), categories = ['batting', 'pitching'] } = params;
    const response = await apiClient.post('/v2/teams/compare', {
      teams,
      year,
      categories
    });
    return response.data;
  }
};



// ============================================================================
// MACRO SPLITS API (v2 canonical)
// ============================================================================

export const macroSplitsApi = {
  // Get full player macro splits object, a subtree via ?path=, or multiple subtrees via paths
  // pathOrOptions can be a string path or an options object: { path, paths: string[], compact: boolean, maxDepth?: number }
  getPlayerMacro: async (team, player, season = CURRENT_YEAR(), pathOrOptions) => {
  // Normalize to underscores (backend normalizes similarly) to improve cache hit rate
  const safePlayer = encodeURIComponent(player.replace(/\s+/g, '_').replace(/-+/g,'_'));
    const params = new URLSearchParams();
    if (typeof pathOrOptions === 'string') {
      params.set('path', pathOrOptions);
      params.set('compact', 'true'); // default compact for subtree
    } else if (pathOrOptions && typeof pathOrOptions === 'object') {
      const { path, paths, compact, maxDepth, gameType, career } = pathOrOptions;
      if (path) params.set('path', path);
      if (paths && Array.isArray(paths) && paths.length) params.set('paths', paths.join(','));
      if (compact != null) params.set('compact', String(Boolean(compact)));
      if (maxDepth != null) params.set('maxDepth', String(maxDepth));
      if (gameType && gameType !== 'all') params.set('gameType', gameType);
      if (career) params.set('career', 'true');
    }
    const qs = params.toString() ? `?${params.toString()}` : '';
    const response = await apiClient.get(`/v2/splits/macro/player/${team}/${safePlayer}/${season}${qs}`);
    // When path(s) provided, backend may wrap results under { data } or { results }
    if (typeof pathOrOptions === 'string' || (pathOrOptions && pathOrOptions.path)) {
      return response.data?.data ?? null;
    }
    if (pathOrOptions && Array.isArray(pathOrOptions.paths)) {
      return response.data?.results ?? [];
    }
    return response.data;
  },

  // Get full team macro splits object, a subtree via ?path=, or multi-path batch
  getTeamMacro: async (team, season = CURRENT_YEAR(), pathOrOptions) => {
    const params = new URLSearchParams();
    if (typeof pathOrOptions === 'string') {
      params.set('path', pathOrOptions);
      params.set('compact', 'true');
    } else if (pathOrOptions && typeof pathOrOptions === 'object') {
      const { path, paths, compact, maxDepth } = pathOrOptions;
      if (path) params.set('path', path);
      if (paths && Array.isArray(paths) && paths.length) params.set('paths', paths.join(','));
      if (compact != null) params.set('compact', String(Boolean(compact)));
      if (maxDepth != null) params.set('maxDepth', String(maxDepth));
    }
    const qs = params.toString() ? `?${params.toString()}` : '';
    const response = await apiClient.get(`/v2/splits/macro/team/${team}/${season}${qs}`);
    if (typeof pathOrOptions === 'string' || (pathOrOptions && pathOrOptions.path)) {
      return response.data?.data ?? null;
    }
    if (pathOrOptions && Array.isArray(pathOrOptions.paths)) {
      return response.data?.results ?? [];
    }
    return response.data;
  }
};

// ============================================================================
// PITCH-LEVEL SPLITS API (v2)
// ============================================================================

export const pitchSplitsApi = {
  // Get full pitch profile for a batter (pitch types, zones, batted ball)
  getBatterPitchData: async (team, player, season = CURRENT_YEAR(), { gameType } = {}) => {
    const safePlayer = encodeURIComponent(player.replace(/\s+/g, '_').replace(/-+/g, '_'));
    const params = new URLSearchParams();
    if (gameType) params.set('gameType', gameType);
    const qs = params.toString();
    const response = await apiClient.get(`/v2/pitch-splits/batter/${team}/${safePlayer}/${season}${qs ? '?' + qs : ''}`);
    return response.data?.data || null;
  },

  // Get full pitch profile for a pitcher (pitch types, zones, batted ball)
  getPitcherPitchData: async (team, player, season = CURRENT_YEAR(), { gameType } = {}) => {
    const safePlayer = encodeURIComponent(player.replace(/\s+/g, '_').replace(/-+/g, '_'));
    const params = new URLSearchParams();
    if (gameType) params.set('gameType', gameType);
    const qs = params.toString();
    const response = await apiClient.get(`/v2/pitch-splits/pitcher/${team}/${safePlayer}/${season}${qs ? '?' + qs : ''}`);
    return response.data?.data || null;
  },

  // Get zone heatmap data
  // metric: 'pitches' | 'swingRate' | 'whiffRate' | 'hitRate'
  getZoneHeatmap: async (perspective, team, player, season = CURRENT_YEAR(), metric = 'pitches', pitchType = null, { gameType } = {}) => {
    const safePlayer = encodeURIComponent(player.replace(/\s+/g, '_').replace(/-+/g, '_'));
    const params = new URLSearchParams({ metric });
    if (pitchType) params.set('pitchType', pitchType);
    if (gameType) params.set('gameType', gameType);
    const response = await apiClient.get(`/v2/pitch-splits/zone-heatmap/${perspective}/${team}/${safePlayer}/${season}?${params.toString()}`);
    return response.data;
  },

  // Get detailed stats for a specific pitch type
  getPitchTypeDetail: async (perspective, team, player, season, pitchType, { gameType } = {}) => {
    const safePlayer = encodeURIComponent(player.replace(/\s+/g, '_').replace(/-+/g, '_'));
    const params = new URLSearchParams();
    if (gameType) params.set('gameType', gameType);
    const qs = params.toString();
    const response = await apiClient.get(`/v2/pitch-splits/pitch-type/${perspective}/${team}/${safePlayer}/${season}/${pitchType}${qs ? '?' + qs : ''}`);
    return response.data;
  },

  // Search players with pitch data
  searchPitchPlayers: async (query, params = {}) => {
    const { perspective = 'batter', season = CURRENT_YEAR(), limit = 25 } = params;
    const qs = new URLSearchParams({ q: query, perspective, season, limit: String(limit) });
    const response = await apiClient.get(`/v2/pitch-splits/search?${qs.toString()}`);
    return response.data;
  },

  // Get individual pitch events with filtering
  // filters: { pitchType, result, hand, game, zone, inning, abResult, minVelo, maxVelo, homeAway, opponent, limit, gameType }
  getPitchEvents: async (perspective, team, player, season = CURRENT_YEAR(), filters = {}) => {
    const safePlayer = encodeURIComponent(player.replace(/\s+/g, '_').replace(/-+/g, '_'));
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => { if (v != null && v !== '') params.set(k, String(v)); });
    const response = await apiClient.get(`/v2/pitch-splits/pitch-events/${perspective}/${team}/${safePlayer}/${season}?${params.toString()}`);
    return response.data;
  },

  // Get pitch profile split by opponent handedness
  getVsHand: async (perspective, team, player, season = CURRENT_YEAR(), hand = 'L', { gameType } = {}) => {
    const safePlayer = encodeURIComponent(player.replace(/\s+/g, '_').replace(/-+/g, '_'));
    const params = new URLSearchParams();
    if (gameType) params.set('gameType', gameType);
    const qs = params.toString();
    const response = await apiClient.get(`/v2/pitch-splits/vs-hand/${perspective}/${team}/${safePlayer}/${season}/${hand}${qs ? '?' + qs : ''}`);
    return response.data?.data || null;
  },

  // Get per-game pitch usage breakdown
  getGameLog: async (perspective, team, player, season = CURRENT_YEAR(), { gameType } = {}) => {
    const safePlayer = encodeURIComponent(player.replace(/\s+/g, '_').replace(/-+/g, '_'));
    const params = new URLSearchParams();
    if (gameType) params.set('gameType', gameType);
    const qs = params.toString();
    const response = await apiClient.get(`/v2/pitch-splits/game-log/${perspective}/${team}/${safePlayer}/${season}${qs ? '?' + qs : ''}`);
    return response.data;
  },

  // Get reconstructed at-bats with pitch sequences
  // filters: { resultFilter, opponent, hand, game, inning, gameDate, pitchType, homeAway, page, pageSize, gameType }
  getAtBats: async (perspective, team, player, season = CURRENT_YEAR(), filters = {}) => {
    const safePlayer = encodeURIComponent(player.replace(/\s+/g, '_').replace(/-+/g, '_'));
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => { if (v != null && v !== '') params.set(k, String(v)); });
    const response = await apiClient.get(`/v2/pitch-splits/at-bats/${perspective}/${team}/${safePlayer}/${season}?${params.toString()}`);
    return response.data;
  },

  // Get dynamic league baselines for a season (per-pitch-type averages & stddevs)
  getBaselines: async (season = CURRENT_YEAR()) => {
    const response = await apiClient.get(`/v2/pitch-splits/baselines/${season}`);
    return response.data;
  },

  // Get Stuff+ leaderboard
  // options: { pitchType, limit, minPitches, gameType }
  getStuffPlusLeaderboard: async (season = CURRENT_YEAR(), options = {}) => {
    const params = new URLSearchParams({ season });
    if (options.pitchType) params.set('pitchType', options.pitchType);
    if (options.limit) params.set('limit', String(options.limit));
    if (options.minPitches) params.set('minPitches', String(options.minPitches));
    if (options.gameType) params.set('gameType', options.gameType);
    const response = await apiClient.get(`/v2/pitch-splits/leaderboard/stuff-plus?${params.toString()}`);
    return response.data;
  },

  // Get Pitch Lab designer corpus (all qualified pitcher-pitchtype rows +
  // league baselines + Stuff+ weights) for client-side interactive scoring
  getLabCorpus: async (season = CURRENT_YEAR(), options = {}) => {
    const params = new URLSearchParams();
    if (options.minPitches) params.set('minPitches', String(options.minPitches));
    const qs = params.toString();
    const response = await apiClient.get(`/v2/pitch-splits/lab/corpus/${season}${qs ? '?' + qs : ''}`);
    return response.data;
  },

  // Get pitch sequencing analysis (transition matrix, count patterns, first pitch tendencies)
  getSequencing: async (perspective, team, player, season = CURRENT_YEAR(), { gameType } = {}) => {
    const safePlayer = encodeURIComponent(player.replace(/\s+/g, '_').replace(/-+/g, '_'));
    const params = new URLSearchParams();
    if (gameType) params.set('gameType', gameType);
    const qs = params.toString();
    const response = await apiClient.get(`/v2/pitch-splits/sequencing/${perspective}/${team}/${safePlayer}/${season}${qs ? '?' + qs : ''}`);
    return response.data;
  },

  // Get pitch tunneling analysis (release point similarity, movement differentials)
  getTunneling: async (perspective, team, player, season = CURRENT_YEAR(), { gameType } = {}) => {
    const safePlayer = encodeURIComponent(player.replace(/\s+/g, '_').replace(/-+/g, '_'));
    const params = new URLSearchParams();
    if (gameType) params.set('gameType', gameType);
    const qs = params.toString();
    const response = await apiClient.get(`/v2/pitch-splits/tunneling/${perspective}/${team}/${safePlayer}/${season}${qs ? '?' + qs : ''}`);
    return response.data;
  },

  // Get expected stats (xBA, xSLG) from batted ball data
  getExpectedStats: async (perspective, team, player, season = CURRENT_YEAR(), { gameType } = {}) => {
    const safePlayer = encodeURIComponent(player.replace(/\s+/g, '_').replace(/-+/g, '_'));
    const params = new URLSearchParams();
    if (gameType) params.set('gameType', gameType);
    const qs = params.toString();
    const response = await apiClient.get(`/v2/pitch-splits/expected-stats/${perspective}/${team}/${safePlayer}/${season}${qs ? '?' + qs : ''}`);
    return response.data;
  },
};

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

export const apiUtils = {
  // Format player ID for API calls
  formatPlayerId: (team, firstName, lastName, year = CURRENT_YEAR()) => {
    const formattedName = `${firstName}_${lastName}`.replace(/\s+/g, '_').toUpperCase();
    return `${team.toUpperCase()}-${formattedName}-${year}`;
  },

  // Parse player ID to components
  parsePlayerId: (playerId) => {
    const parts = playerId.split('-');
    if (parts.length >= 3) {
      const team = parts[0];
      const year = parts[parts.length - 1];
      const nameParts = parts.slice(1, -1);
      const name = nameParts.join(' ').replace(/_/g, ' ');
      return { team, name, year };
    }
    return null;
  },

  // Format statistical values for display
  formatStat: (value, type = 'decimal', decimals = 3) => {
    if (value === null || value === undefined || isNaN(value)) return '-';
    
    switch (type) {
      case 'percentage':
        return `${(value * 100).toFixed(1)}%`;
      case 'decimal':
        return Number(value).toFixed(decimals);
      case 'integer':
        return Math.round(value);
      case 'era':
        return Number(value).toFixed(2);
      case 'avg':
        return value >= 1 ? '1.000' : Number(value).toFixed(3).substring(1);
      default:
        return value.toString();
    }
  },

  // Get team color for UI
  getTeamColor: (teamId) => {
    const teamColors = {
      'NYY': '#132448',
      'BOS': '#BD3039',
      'TOR': '#134A8E',
      'TB': '#092C5C',
      'BAL': '#DF4601',
      'HOU': '#002D62',
      'LAA': '#BA0021',
      'OAK': '#003831',
      'SEA': '#0C2C56',
      'TEX': '#003278',
      'ATL': '#CE1141',
      'WSH': '#AB0003',
      'NYM': '#002D72',
      'PHI': '#E81828',
      'MIA': '#00A3E0',
      'CHC': '#0E3386',
      'MIL': '#12284B',
      'STL': '#C41E3A',
      'CIN': '#C6011F',
      'PIT': '#FDB827',
      'LAD': '#005A9C',
      'SD': '#2F241D',
      'SF': '#FD5A1E',
      'COL': '#C4CED4',
      'ARI': '#A71930'
    };
    return teamColors[teamId?.toUpperCase()] || '#666666';
  },

  // Statistical category configurations
  getStatCategories: () => ({
    batting: {
      basic: ['avg', 'obp', 'slg', 'ops', 'hits', 'homeRuns', 'rbi', 'runs'],
      advanced: ['iso', 'babip', 'woba', 'wrcPlus', 'kRate', 'bbRate'],
      counting: ['atBats', 'plateAppearances', 'doubles', 'triples', 'stolenBases']
    },
    pitching: {
      basic: ['era', 'whip', 'wins', 'saves', 'strikeOuts', 'inningsPitched'],
      advanced: ['fip', 'strikeoutsPer9Inn', 'walksPer9Inn', 'hitsPer9Inn', 'strikeoutWalkRatio'],
      counting: ['battersFaced', 'hits', 'earnedRuns', 'baseOnBalls', 'hitByPitch']
    },
    fielding: {
      basic: ['fieldingPercentage', 'assists', 'putOuts', 'errors'],
      advanced: ['chances', 'range', 'zone'],
      counting: ['games', 'innings', 'doublePlays']
    }
  })
};

// ===========================================================================
// TRADE API — Trade value calculations, mock trade evaluation, search
// ===========================================================================
export const tradeApi = {
  /** Get trade value for a single player */
  getPlayerTradeValue: async (playerId, year = CURRENT_YEAR(), { mode } = {}) => {
    const params = { year };
    if (mode) params.mode = mode;
    const res = await apiClient.get(`/v2/trade/value/${encodeURIComponent(playerId)}`, { params });
    return res.data;
  },

  /** Get all players on a team with trade values */
  getTeamTradeAssets: async (teamId, year = CURRENT_YEAR(), { mode } = {}) => {
    const params = { year };
    if (mode) params.mode = mode;
    const res = await apiClient.get(`/v2/trade/team/${encodeURIComponent(teamId)}`, { params });
    return res.data;
  },

  /** Trade value leaderboard */
  getTradeLeaders: async ({ year, limit = 50, minGames = 10, category = 'all', mode } = {}) => {
    const params = { year: year || CURRENT_YEAR(), limit, minGames, category };
    if (mode) params.mode = mode;
    const res = await apiClient.get('/v2/trade/leaders', { params });
    return res.data;
  },

  /** Evaluate a mock trade — sideA and sideB are arrays of player IDs */
  evaluateTrade: async (sideA, sideB, year = CURRENT_YEAR(), { mode } = {}) => {
    const body = { sideA, sideB, year };
    if (mode) body.mode = mode;
    const res = await apiClient.post('/v2/trade/evaluate', body);
    return res.data;
  },

  /** Search players by name for trade builder autocomplete */
  searchPlayers: async (query, { year, team, limit = 15, mode } = {}) => {
    const params = { q: query, year: year || CURRENT_YEAR(), team, limit };
    if (mode) params.mode = mode;
    const res = await apiClient.get('/v2/trade/search', { params });
    return res.data;
  },

  /** Head-to-head trade value comparison */
  compareTradeValues: async (playerA, playerB, year = CURRENT_YEAR(), { mode } = {}) => {
    const params = { year };
    if (mode) params.mode = mode;
    const res = await apiClient.get(`/v2/trade/compare/${encodeURIComponent(playerA)}/${encodeURIComponent(playerB)}`, { params });
    return res.data;
  },

  // ── Positional Benchmarks ──

  /** Alias — get DTV breakdown for a single player (same endpoint as getPlayerTradeValue) */
  getPlayerDTV: async (playerId, year = CURRENT_YEAR(), { mode } = {}) => tradeApi.getPlayerTradeValue(playerId, year, { mode }),

  /** Alias — get all players on a team with DTV breakdown (same endpoint as getTeamTradeAssets) */
  getTeamDTV: async (teamId, year = CURRENT_YEAR(), { mode } = {}) => tradeApi.getTeamTradeAssets(teamId, year, { mode }),

  /** Get league-wide positional production & market benchmarks */
  getBenchmarks: async ({ year, minGames = 10, mode } = {}) => {
    const params = { year: year || CURRENT_YEAR(), minGames };
    if (mode) params.mode = mode;
    const res = await apiClient.get('/v2/trade/benchmarks', { params });
    return res.data;
  },

  /** Get positional trade target suggestions — filter by position, team, exclusions */
  getSuggestions: async ({ position, team, excludeTeam, excludePlayers, minTV, maxTV, minGames = 5, limit = 25, year } = {}) => {
    const params = { year: year || CURRENT_YEAR(), limit, minGames };
    if (position) params.position = position;
    if (team) params.team = team;
    if (excludeTeam) params.excludeTeam = excludeTeam;
    if (excludePlayers && excludePlayers.length) params.excludePlayers = excludePlayers.join(',');
    if (minTV != null) params.minTV = minTV;
    if (maxTV != null) params.maxTV = maxTV;
    const res = await apiClient.get('/v2/trade/suggestions', { params });
    return res.data;
  },
};

// ============================================================================
// ADVANCED ANALYTICS API (v2) — Park Factors, CVR v5, WAR v2, Matchups
// ============================================================================

export const analyticsApi = {
  /** Get park factors for all stadiums or a specific team */
  getParkFactors: async (params = {}) => {
    const { team, year = CURRENT_YEAR() } = params;
    const qs = new URLSearchParams({ ...(team && { team }), ...(year && { year }) });
    const res = await apiClient.get(`/v2/analytics/park-factors?${qs}`);
    return res.data;
  },

  /** Get comprehensive player profile with CVR v5, WAR v2, trade value */
  getPlayerProfile: async (playerId, year = CURRENT_YEAR()) => {
    const res = await apiClient.get(`/v2/analytics/player-profile/${encodeURIComponent(playerId)}?year=${year}`);
    return res.data;
  },

  /** Get matchup analysis between player and target team (for trade scenarios) */
  getMatchupAnalysis: async (player, fromTeam, toTeam, year = CURRENT_YEAR()) => {
    const qs = new URLSearchParams({ player, fromTeam, toTeam, year });
    const res = await apiClient.get(`/v2/analytics/matchup?${qs}`);
    return res.data;
  },

  /** Advanced leaderboard with new metrics (wOBA, wRC+, FIP, ISO, etc.) */
  getAdvancedLeaderboard: async (params = {}) => {
    const { category = 'batting', sortBy = 'war', limit = 50, minGames = 20, year = CURRENT_YEAR(), mode } = params;
    const qp = { category, sortBy, limit: String(limit), minGames: String(minGames), year };
    if (mode) qp.mode = mode;
    const qs = new URLSearchParams(qp);
    const res = await apiClient.get(`/v2/analytics/leaderboard?${qs}`);
    return res.data;
  },

  /** Get comprehensive team analysis with WAR distribution, needs */
  getTeamAnalysis: async (team, year = CURRENT_YEAR()) => {
    const res = await apiClient.get(`/v2/analytics/team-analysis/${encodeURIComponent(team)}?year=${year}`);
    return res.data;
  },

  /** Evaluate a mock trade using advanced CVR v5 + WAR v2 engine */
  evaluateAdvancedTrade: async (sideA, sideB, year = CURRENT_YEAR()) => {
    const res = await apiClient.post('/v2/analytics/trade-evaluate', { sideA, sideB, year });
    return res.data;
  },

  /** Get league-wide context stats */
  getLeagueContext: async (year = CURRENT_YEAR()) => {
    const res = await apiClient.get(`/v2/analytics/league-context?year=${year}`);
    return res.data;
  }
};

// Health check function
export const healthCheck = async () => {
  try {
    const [health, redisHealth] = await Promise.all([
      apiClient.get('/health'),
      apiClient.get('/redis-health')
    ]);
    
    return {
      api: health.data,
      redis: redisHealth.data,
      status: 'healthy'
    };
  } catch (error) {
    return {
      status: 'error',
      error: error.message
    };
  }
};

// ============================================================================
// MATCHUP SPLITS API (v2)
// ============================================================================

export const matchupsApi = {
  // Get head-to-head: how a batter performs against a specific pitcher
  getBatterVsPitcher: async (team, batter, pitcherTeam, pitcher, season = CURRENT_YEAR()) => {
    const params = new URLSearchParams({
      team, batter: batter.replace(/\s+/g, '_'), pitcher: pitcher.replace(/\s+/g, '_'), season,
      ...(pitcherTeam && { pitcherTeam }),
    });
    const response = await apiClient.get(`/v2/matchups/batter-vs-pitcher?${params}`);
    return response.data;
  },

  // Get player performance against a specific opposing team
  getPlayerVsTeam: async (team, player, opponent, season = CURRENT_YEAR()) => {
    const params = new URLSearchParams({
      team, player: player.replace(/\s+/g, '_'), opponent, season,
    });
    const response = await apiClient.get(`/v2/matchups/player-vs-team?${params}`);
    return response.data;
  },

  // Full game preview: how all batters on a team perform against a pitcher
  getGamePreview: async (team, opponent, opponentPitcher, season = CURRENT_YEAR()) => {
    const params = new URLSearchParams({
      team, opponentPitcher: opponentPitcher.replace(/\s+/g, '_'), season,
      ...(opponent && { opponent }),
    });
    const response = await apiClient.get(`/v2/matchups/game-preview?${params}`);
    return response.data;
  },

  // How our pitcher performs against the opposing team's lineup
  getPitcherVsLineup: async (team, pitcher, opponent, season = CURRENT_YEAR()) => {
    const params = new URLSearchParams({
      team, pitcher: pitcher.replace(/\s+/g, '_'), season,
      ...(opponent && { opponent }),
    });
    const response = await apiClient.get(`/v2/matchups/pitcher-vs-lineup?${params}`);
    return response.data;
  },
};

export const awardsApi = {
  // Single endpoint — returns AL & NL races for MVP, Cy Young, ROY in one call.
  getAwards: async ({ year = CURRENT_YEAR(), limit = 10 } = {}) => {
    const params = new URLSearchParams({ year: String(year), limit: String(limit) });
    return cachedGet(`/v2/awards?${params.toString()}`);
  },
};

export const nlqApi = {
  ask: async (question, year, conversationHistory = []) => {
    const token = localStorage.getItem('tc-auth-token');
    const headers = token ? { Authorization: `Bearer ${token}` } : {};
    const response = await apiClient.post('/v2/nlq', { question, year, conversationHistory }, { headers });
    return response.data;
  },
  // Streaming version — calls onToken(text) for each chunk, resolves with full envelope on done.
  askStream: async (question, year, conversationHistory = [], { onToken, signal } = {}) => {
    const token = localStorage.getItem('tc-auth-token');
    const headers = { 'Content-Type': 'application/json' };
    if (token) headers['Authorization'] = `Bearer ${token}`;

    const res = await fetch(`${API_BASE_URL}/v2/nlq/stream`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ question, year, conversationHistory }),
      signal,
    });

    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      const err = new Error(body.error || `HTTP ${res.status}`);
      err.response = { status: res.status, data: body };
      throw err;
    }

    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    let buf = '';

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buf += decoder.decode(value, { stream: true });
      const lines = buf.split('\n');
      buf = lines.pop(); // keep incomplete last line

      for (const line of lines) {
        if (!line.startsWith('data: ')) continue;
        const raw = line.slice(6).trim();
        if (!raw) continue;
        let msg;
        try { msg = JSON.parse(raw); } catch { continue; }

        if (msg.type === 'token' && onToken) {
          onToken(msg.text);
        } else if (msg.type === 'done') {
          return msg; // full envelope
        } else if (msg.type === 'error') {
          const err = new Error(msg.message || 'Stream error');
          throw err;
        }
      }
    }
  },
  examples: async () => {
    const response = await apiClient.get('/v2/nlq/examples');
    return response.data;
  },
  status: async () => {
    const response = await apiClient.get('/v2/nlq/status');
    return response.data;
  },
};

// ── Auth API ─────────────────────────────────────────────────────────────────
export const authApi = {
  register: (email, password) => apiClient.post('/auth/register', { email, password }),
  login: (email, password) => apiClient.post('/auth/login', { email, password }),
  me: (token) => apiClient.get('/auth/me', { headers: { Authorization: `Bearer ${token}` } }),
  forgotPassword: (email) => apiClient.post('/auth/forgot-password', { email }),
  resetPassword: (token, password) => apiClient.post('/auth/reset-password', { token, password }),
  verifyEmail: (token) => apiClient.post('/auth/verify-email', { token }),
  resendVerification: (token) => apiClient.post('/auth/resend-verification', {}, { headers: { Authorization: `Bearer ${token}` } }),
};

// ── Payments API ──────────────────────────────────────────────────────────────
export const paymentsApi = {
  checkout: (token) => apiClient.post('/payments/checkout', {}, { headers: { Authorization: `Bearer ${token}` } }),
  portal: (token) => apiClient.post('/payments/portal', {}, { headers: { Authorization: `Bearer ${token}` } }),
  redeem: (token, code) => apiClient.post('/payments/redeem', { code }, { headers: { Authorization: `Bearer ${token}` } }),
  status: (token) => apiClient.get('/payments/status', { headers: { Authorization: `Bearer ${token}` } }),
};

// ── Admin API ─────────────────────────────────────────────────────────────
export const adminApi = {
  stats: (token) => apiClient.get('/admin/stats', { headers: { Authorization: `Bearer ${token}` } }),
  sendTestDigest: (token, email) => apiClient.post('/admin/send-test-digest', { email }, { headers: { Authorization: `Bearer ${token}` } }),
  grantPro: (token, email, days) => apiClient.post('/admin/users/grant-pro', { email, days }, { headers: { Authorization: `Bearer ${token}` } }),
  removeProTime: (token, email, days) => apiClient.post('/admin/users/remove-pro-time', { email, days }, { headers: { Authorization: `Bearer ${token}` } }),
  revokePro: (token, email) => apiClient.post('/admin/users/revoke-pro', { email }, { headers: { Authorization: `Bearer ${token}` } }),
  setAdmin: (token, email, isAdmin) => apiClient.post('/admin/users/set-admin', { email, isAdmin }, { headers: { Authorization: `Bearer ${token}` } }),
  deleteUser: (token, email) => apiClient.delete('/admin/users', { data: { email }, headers: { Authorization: `Bearer ${token}` } }),
  listInvites: (token) => apiClient.get('/admin/invites', { headers: { Authorization: `Bearer ${token}` } }),
  createInvites: (token, { count, label, days }) => apiClient.post('/admin/invites', { count, label, days }, { headers: { Authorization: `Bearer ${token}` } }),
  deleteInvite: (token, code) => apiClient.delete('/admin/invites', { data: { code }, headers: { Authorization: `Bearer ${token}` } }),
};

// eslint-disable-next-line import/no-anonymous-default-export
export default {
  statsApi,
  playersApi,
  teamsApi,
  macroSplitsApi,
  matchupsApi,
  tradeApi,
  analyticsApi,
  awardsApi,
  nlqApi,
  authApi,
  paymentsApi,
  adminApi,
  healthCheck
};
